import React, { useContext } from 'react'
import { Package, Users, Target, Eye } from 'lucide-react'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { getDevToolIcon } from '@/components/utils/taskCreationUtils'
import { RoadmapContext } from '@/context/RoadmapContext'

interface RoadmapHeaderProps {
  selectedProductId: string
  onProductChange: (productId: string) => void
}

const RoadmapHeader = ({ selectedProductId, onProductChange }: RoadmapHeaderProps) => {
  const { products, selectedProduct } = useContext(RoadmapContext)

  const getFeedbackFocusLabel = (value: string) => {
    const options = {
      '30days': 'Past 30 Days',
      '90days': 'Past 90 Days',
      year: 'Past Year',
      'all-time': 'All Time',
    }
    return options[value as keyof typeof options] || 'Past 90 Days'
  }

  return (
    <div className="px-8 pb-4 pt-8 w-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="flex flex-col gap-1.5">
            <Select value={selectedProduct.id} onValueChange={onProductChange}>
              <SelectTrigger className="w-fit min-w-[180px] bg-white text-base">
                <Package className="w-5 h-5 mr-2 text-[#FF4A8E]" />
                <SelectValue placeholder="Select product" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center self-end h-10">
            <div className="flex items-center gap-1.5">
              <Users className="h-4 w-4 text-[#9F9EA1] dark:text-gray-400" />
              <span className="text-sm text-[#40424A] dark:text-gray-200">
                {selectedProduct.configs?.capacity}{' '}
                {Number(selectedProduct.configs?.capacity) === 1 ? 'Parallel Opportunity' : 'Parallel Opportunities'}
              </span>
            </div>

            <span className="mx-2 text-[#9F9EA1] dark:text-gray-400">・</span>

            <div className="flex items-center gap-1.5">
              <img
                src={getDevToolIcon(selectedProduct.configs?.dev_tool as 'jira' | 'asana' | 'monday' | 'productboard')}
                alt={selectedProduct.configs?.dev_tool}
                className="h-4 w-auto"
              />
              <span className="text-sm text-[#40424A] dark:text-gray-200 capitalize">{selectedProduct.configs?.dev_tool}</span>
            </div>

            <span className="mx-2 text-[#9F9EA1] dark:text-gray-400">・</span>

            <div className="flex items-center gap-1.5">
              <Target className="h-4 w-4 text-[#9F9EA1] dark:text-gray-400" />
              <span className="text-sm text-[#40424A] dark:text-gray-200">
                {selectedProduct.configs?.objectives?.length} {selectedProduct.configs?.objectives?.length === 1 ? 'Objective' : 'Objectives'} Selected
              </span>
            </div>

            <span className="mx-2 text-[#9F9EA1] dark:text-gray-400">・</span>

            <div className="flex items-center gap-1.5">
              <Eye className="h-4 w-4 text-[#9F9EA1] dark:text-gray-400" />
              <span className="text-sm text-[#40424A] dark:text-gray-200">{getFeedbackFocusLabel(selectedProduct.configs?.feedback_focus as '30days' | '90days' | 'year' | 'all-time')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RoadmapHeader
